import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  Target, 
  Users, 
  Award, 
  Shield,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const Section = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const ChiSiamoPage = () => {
  const values = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Sicurezza',
      description: 'La sicurezza degli operatori è la nostra priorità assoluta. Ogni progetto è pensato per garantire la massima protezione.'
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Precisione',
      description: 'Progettiamo e installiamo con la massima precisione, rispettando ogni dettaglio delle normative vigenti.'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Qualità',
      description: 'Utilizziamo solo materiali certificati e componenti di alta qualità per garantire durabilità e affidabilità.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Competenza',
      description: 'Il nostro team è composto da professionisti formati e certificati secondo le più recenti normative.'
    }
  ];

  const milestones = [
    { year: '1993', event: 'Fondazione di Revogreen Italia' },
    { year: '2005', event: 'Espansione servizi impermeabilizzazione' },
    { year: '2012', event: 'Prima certificazione UNI EN 795' },
    { year: '2015', event: 'Avvio divisione linee vita RevoQuota' },
    { year: '2018', event: '1000+ installazioni completate' },
    { year: '2021', event: 'Certificazione installatori UNI 11900' },
    { year: '2024', event: 'Costituzione Revogreen srl - Espansione servizi revisione' }
  ];

  return (
    <div className="bg-revo-bg pt-20">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="font-manrope text-sm text-revo-orange uppercase tracking-widest">
                Chi Siamo
              </span>
              <h1 className="font-barlow text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white mt-4 mb-6">
                Passione Per<br />
                <span className="text-gray-500">La Sicurezza</span>
              </h1>
              <p className="font-manrope text-lg text-gray-300 leading-relaxed mb-8">
                RevoQuota è la divisione di Revogreen srl dedicata alla sicurezza dei lavori in quota. 
                Con oltre 30 anni di esperienza dal 1993, siamo specializzati nella progettazione, installazione 
                e manutenzione di sistemi anticaduta per ogni tipologia di copertura.
              </p>
              <Link
                to="/contatti"
                data-testid="chi-siamo-cta"
                className="inline-flex items-center gap-3 bg-revo-orange text-white px-6 py-3 font-barlow font-bold uppercase tracking-widest text-sm hover:bg-revo-orange-dark transition-all duration-300"
              >
                Contattaci
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-square overflow-hidden border border-revo-border">
                <img 
                  src="https://customer-assets.emergentagent.com/job_lifeline-solutions/artifacts/5az7lvks_linee-vita-installazione-ottimale.jpg"
                  alt="Team RevoQuota - Installazione linee vita"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-revo-orange p-6">
                <div className="font-barlow text-4xl font-bold text-white">30+</div>
                <div className="font-manrope text-sm text-white/80">Anni di Esperienza</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <Section className="py-24 bg-revo-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <span className="font-manrope text-sm text-revo-orange uppercase tracking-widest">
              I Nostri Valori
            </span>
            <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mt-4">
              Cosa Ci Guida
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-revo-bg border border-revo-border p-8 group hover:border-revo-orange/50 transition-colors"
                data-testid={`value-card-${index}`}
              >
                <div className="w-14 h-14 bg-revo-orange/10 flex items-center justify-center text-revo-orange mb-6 group-hover:bg-revo-orange group-hover:text-white transition-all">
                  {value.icon}
                </div>
                <h3 className="font-barlow text-xl font-bold uppercase tracking-wide text-white mb-3">
                  {value.title}
                </h3>
                <p className="font-manrope text-gray-400 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Mission */}
      <Section className="py-24 bg-revo-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-manrope text-sm text-revo-orange uppercase tracking-widest">
                La Nostra Missione
              </span>
              <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mt-4 mb-6">
                Proteggere Chi<br />
                <span className="text-revo-orange">Lavora In Alto</span>
              </h2>
              <p className="font-manrope text-gray-400 leading-relaxed mb-6">
                La nostra missione è fornire soluzioni di sicurezza anticaduta all'avanguardia, 
                garantendo che ogni lavoratore possa operare in quota con la massima protezione. 
                Crediamo che la sicurezza non sia un costo, ma un investimento fondamentale.
              </p>
              <ul className="space-y-3">
                {[
                  'Ridurre a zero gli infortuni da caduta',
                  'Diffondere la cultura della sicurezza',
                  'Innovare i sistemi di protezione',
                  'Formare professionisti competenti'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-revo-orange flex-shrink-0" />
                    <span className="font-manrope text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] overflow-hidden border border-revo-border">
                <img 
                  src="https://customer-assets.emergentagent.com/job_lifeline-solutions/artifacts/z20hyi6z_OIP%20%283%29.jpg"
                  alt="Operatore su linea vita"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Timeline */}
      <Section className="py-24 bg-revo-paper">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <span className="font-manrope text-sm text-revo-orange uppercase tracking-widest">
              La Nostra Storia
            </span>
            <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mt-4">
              Il Nostro Percorso
            </h2>
          </div>

          <div className="relative">
            {/* Line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-revo-border" />
            
            {/* Milestones */}
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div key={index} className="relative flex gap-8" data-testid={`milestone-${index}`}>
                  <div className="relative z-10 w-16 h-16 bg-revo-bg border border-revo-border flex items-center justify-center shrink-0">
                    <span className="font-barlow font-bold text-revo-orange">{milestone.year}</span>
                  </div>
                  <div className="pt-4">
                    <p className="font-manrope text-white">{milestone.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Company Info */}
      <Section className="py-24 bg-revo-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="bg-revo-paper border border-revo-border p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-barlow text-2xl font-bold uppercase tracking-wide text-white mb-4">
                  Dati Aziendali
                </h3>
                <div className="space-y-3 font-manrope text-gray-400">
                  <p><span className="text-gray-300">Ragione Sociale:</span> Revogreen srl</p>
                  <p><span className="text-gray-300">P.IVA:</span> 05121010234</p>
                  <p><span className="text-gray-300">Sede Legale:</span> Via Alberto Dominutti, 6</p>
                  <p>37135 Verona (VR)</p>
                </div>
              </div>
              <div>
                <h3 className="font-barlow text-2xl font-bold uppercase tracking-wide text-white mb-4">
                  Certificazioni
                </h3>
                <ul className="space-y-2">
                  {[
                    'Installatori certificati UNI 11900:2023',
                    'Sistemi conformi UNI EN 795',
                    'Parapetti conformi UNI EN 13374',
                    'Formazione continua del personale'
                  ].map((cert, index) => (
                    <li key={index} className="flex items-center gap-2 text-gray-400">
                      <CheckCircle2 className="w-4 h-4 text-revo-orange" />
                      <span className="font-manrope text-sm">{cert}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default ChiSiamoPage;
